// src/pages/CustomersPage.js
import React, { useState, useEffect } from 'react';
import { customersAPI } from '../services/api';
import { Link, useNavigate } from 'react-router-dom';

const CustomersPage = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    additional_address: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    postal_code: '',
    city: '',
    notes: '',
    reference_person: ''
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      console.log('Versuche alle Kunden zu laden');
      const response = await customersAPI.getAll();
      console.log(`${response.data.customers.length} Kunden erfolgreich geladen`);
      setCustomers(response.data.customers);
      setError('');
    } catch (err) {
      console.error('Fehler beim Laden der Kunden:', err);
      
      // Prüfen, ob es ein Authentifizierungsfehler ist
      if (err.response && err.response.status === 401) {
        // Manuell zur Login-Seite umleiten, wenn nicht authentifiziert
        console.log('Nicht authentifiziert, leite zur Login-Seite um');
        navigate('/login');
        return; // Wichtig: Return, um weitere Verarbeitung zu stoppen
      }
      
      setError('Fehler beim Laden der Kunden');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Versuche neuen Kunden zu erstellen');
      await customersAPI.create(formData);
      console.log('Kunde erfolgreich erstellt');
      fetchCustomers();
      setShowForm(false);
      setFormData({
        company_name: '',
        additional_address: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        postal_code: '',
        city: '',
        notes: '',
        reference_person: ''
      });
    } catch (err) {
      console.error('Fehler beim Erstellen des Kunden:', err);
      
      // Prüfen, ob es ein Authentifizierungsfehler ist
      if (err.response && err.response.status === 401) {
        // Manuell zur Login-Seite umleiten, wenn nicht authentifiziert
        console.log('Nicht authentifiziert, leite zur Login-Seite um');
        navigate('/login');
        return; // Wichtig: Return, um weitere Verarbeitung zu stoppen
      }
      
      setError('Fehler beim Erstellen des Kunden');
    }
  };

  // Funktion zum sicheren Navigieren zur Detailseite
  const handleNavigateToDetail = (customerId) => {
    // Prüfen, ob ein gültiger Token existiert
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('Kein Token vorhanden - leite zur Login-Seite weiter');
      navigate('/login');
      return;
    }
    navigate(`/kunden/${customerId}`);
  };

  // Funktion zum sicheren Navigieren zur Bearbeitungsseite
  const handleNavigateToEdit = (customerId) => {
    // Prüfen, ob ein gültiger Token existiert
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('Kein Token vorhanden - leite zur Login-Seite weiter');
      navigate('/login');
      return;
    }
    navigate(`/kunden/${customerId}/edit`);
  };

  // Neue Funktion zum Löschen eines Kunden
  const handleDeleteCustomer = async (customerId) => {
    try {
      console.log('Versuche Kunden zu löschen, ID:', customerId);
      await customersAPI.delete(customerId);
      console.log('Kunde erfolgreich gelöscht');
      fetchCustomers(); // Kundenliste neu laden
      setDeleteConfirmation(null); // Dialog schließen
      setError('');
    } catch (err) {
      console.error('Fehler beim Löschen des Kunden:', err);
      
      // Prüfen, ob es ein Authentifizierungsfehler ist
      if (err.response && err.response.status === 401) {
        // Manuell zur Login-Seite umleiten, wenn nicht authentifiziert
        console.log('Nicht authentifiziert, leite zur Login-Seite um');
        navigate('/login');
        return; // Wichtig: Return, um weitere Verarbeitung zu stoppen
      }
      
      setError('Fehler beim Löschen des Kunden');
    }
  };

  if (loading) {
    return <div className="text-center py-6 sm:py-10">Lade Kundendaten...</div>;
  }

  return (
    <div className="p-4 sm:p-0">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
        <h1 className="text-xl sm:text-2xl font-semibold">Kundenverwaltung</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded w-full sm:w-auto"
        >
          {showForm ? 'Abbrechen' : 'Neuer Kunde'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-3 sm:p-4 mb-4 sm:mb-6">
          <p className="text-red-700 text-sm sm:text-base">{error}</p>
        </div>
      )}

      {showForm && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-medium mb-3 sm:mb-4">Neuer Kunde</h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div>
                <label className="block text-gray-700 text-sm sm:text-base mb-1 sm:mb-2">Firma</label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm sm:text-base mb-1 sm:mb-2">Zusätzliche Adresszeile</label>
                <input
                  type="text"
                  name="additional_address"
                  value={formData.additional_address}
                  onChange={handleChange}
                  placeholder="z.B. Abteilung, c/o, Gebäude"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm sm:text-base mb-1 sm:mb-2">Vorname *</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm sm:text-base mb-1 sm:mb-2">Nachname *</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm sm:text-base mb-1 sm:mb-2">E-Mail</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm sm:text-base mb-1 sm:mb-2">Telefon</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm sm:text-base mb-1 sm:mb-2">Referenzperson</label>
                <input
                  type="text"
                  name="reference_person"
                  value={formData.reference_person}
                  onChange={handleChange}
                  placeholder="z.B. daniel.erni@firma.ch"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm sm:text-base mb-1 sm:mb-2">Adresse</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm sm:text-base mb-1 sm:mb-2">PLZ</label>
                <input
                  type="text"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm sm:text-base mb-1 sm:mb-2">Ort</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            <div className="mb-3 sm:mb-4">
              <label className="block text-gray-700 text-sm sm:text-base mb-1 sm:mb-2">Notizen</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                rows="3"
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded w-full sm:w-auto"
              >
                Kunde speichern
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Löschbestätigung Dialog */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h3 className="text-lg font-medium mb-4">Kunde löschen</h3>
            <p className="mb-6">
              Möchten Sie den Kunden "{deleteConfirmation.first_name} {deleteConfirmation.last_name}" wirklich löschen?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirmation(null)}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
              >
                Abbrechen
              </button>
              <button
                onClick={() => handleDeleteCustomer(deleteConfirmation.id)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
        <h2 className="text-lg sm:text-xl font-medium mb-3 sm:mb-4">Kundenliste</h2>
        
        {customers.length === 0 ? (
          <p className="text-gray-500 text-sm sm:text-base">Keine Kunden vorhanden</p>
        ) : (
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="hidden sm:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Firma</th>
                  <th className="hidden md:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kontakt</th>
                  <th className="hidden lg:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adresse</th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {customers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{customer.first_name} {customer.last_name}</div>
                      {/* Mobile-only info */}
                      <div className="sm:hidden text-xs text-gray-500 mt-1">
                        {customer.company_name && <div>{customer.company_name}</div>}
                        {customer.email && <div className="mt-1">{customer.email}</div>}
                        {customer.phone && <div>{customer.phone}</div>}
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{customer.company_name || '-'}</div>
                    </td>
                    <td className="hidden md:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{customer.email || '-'}</div>
                      <div className="text-sm text-gray-500">{customer.phone || '-'}</div>
                    </td>
                    <td className="hidden lg:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{customer.address || '-'}</div>
                      <div className="text-sm text-gray-500">{customer.postal_code} {customer.city}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2 sm:space-x-3">
                        {/* Link durch Button mit onClick-Handler ersetzt */}
                        <button 
                          onClick={() => handleNavigateToDetail(customer.id)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Details
                        </button>
                        {/* Link durch Button mit onClick-Handler ersetzt */}
                        <button
                          onClick={() => handleNavigateToEdit(customer.id)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Bearbeiten
                        </button>
                        <button
                          onClick={() => setDeleteConfirmation(customer)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Löschen
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomersPage;
